import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PageLoader, TableSkeleton } from '../components/Loading';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings, 
  Save, 
  Upload, 
  Trash2, 
  Users, 
  Plus, 
  Pencil, 
  UserX, 
  UserCheck,
  Building2,
  Eye,
  EyeOff
} from 'lucide-react';
import { formatDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_BRAND_LOGO = '/logo.png';

export default function SettingsPage() {
  const { isMD, user: currentUser } = useAuth();
  const { updateSettings } = useSettings();
  const fileInputRef = useRef(null);
  
  // Settings state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_tagline: '',
    company_logo: null,
    referral_commission_rate: 2, // Default 2%
  });

  // User Management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userFormData, setUserFormData] = useState({
    full_name: '',
    email: '',
    role: 'agent',
    password: '',
    status: 'active',
  });
  const [userFormErrors, setUserFormErrors] = useState({});
  const displayedCompanyLogo = formData.company_logo || DEFAULT_BRAND_LOGO;

  useEffect(() => {
    fetchSettings();
    if (isMD) {
      fetchUsers();
    }
  }, [isMD]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/users`);
      // Ensure all users have required fields to prevent rendering errors
      const validUsers = (response.data || []).map(user => ({
        ...user,
        id: user.id || '',
        full_name: user.full_name || 'Unknown',
        email: user.email || '',
        role: user.role || 'agent',
        status: user.status || 'active',
        created_at: user.created_at || null,
      }));
      setUsers(validUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setUsers([]); // Reset to empty array on error
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData({ ...formData, company_logo: e.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, company_logo: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings`, formData);
      updateSettings(response.data);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  // User Management Functions
  const resetUserForm = () => {
    setUserFormData({
      full_name: '',
      email: '',
      role: 'agent',
      password: '',
      status: 'active',
    });
    setUserFormErrors({});
    setSelectedUser(null);
    setShowPassword(false);
  };

  const openAddUserModal = () => {
    resetUserForm();
    setUserModalOpen(true);
  };

  const openEditUserModal = (user) => {
    setSelectedUser(user);
    setUserFormData({
      full_name: user.full_name,
      email: user.email,
      role: user.role,
      password: '',
      status: user.status || 'active',
    });
    setUserFormErrors({});
    setUserModalOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const openDeactivateDialog = (user) => {
    setSelectedUser(user);
    setDeactivateDialogOpen(true);
  };

  const validateUserForm = () => {
    const errors = {};
    if (!userFormData.full_name.trim()) errors.full_name = 'Name is required';
    if (!userFormData.email.trim()) errors.email = 'Email is required';
    if (!userFormData.role) errors.role = 'Role is required';
    if (!selectedUser && !userFormData.password.trim()) errors.password = 'Password is required for new users';
    setUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUserSubmit = async () => {
    if (!validateUserForm()) return;

    setSavingUser(true);
    try {
      const payload = {
        full_name: userFormData.full_name.trim(),
        email: userFormData.email.trim().toLowerCase(),
        role: userFormData.role,
        status: userFormData.status,
      };

      if (userFormData.password.trim()) {
        payload.password = userFormData.password;
      }

      if (selectedUser) {
        await axios.put(`${API}/users/${selectedUser.id}`, payload);
        toast.success('User updated successfully');
      } else {
        payload.password = userFormData.password;
        await axios.post(`${API}/users`, payload);
        toast.success('User created successfully');
      }

      // Close modal and reset form first
      setUserModalOpen(false);
      resetUserForm();
      
      // Then refresh the user list
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save user';
      toast.error(errorMessage);
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleUserStatus = async () => {
    try {
      const endpoint = selectedUser.status === 'active' ? 'deactivate' : 'activate';
      await axios.put(`${API}/users/${selectedUser.id}/${endpoint}`);
      toast.success(`User ${endpoint}d successfully`);
      setDeactivateDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  if (!isMD) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-16">
          <Settings className="w-16 h-16 text-lux-border mb-4" />
          <p className="text-lux-muted text-lg">Only MD can access settings</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-4xl text-lux-text mb-2">Settings</h1>
          <p className="text-lux-muted">Manage company information and users</p>
        </div>

        <Tabs defaultValue="company" className="space-y-6">
          <TabsList className="bg-lux-card border border-lux-border p-1">
            <TabsTrigger
              value="company"
              className="data-[state=active]:bg-lux-gold data-[state=active]:text-lux-black"
              data-testid="tab-company"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Company Info
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="data-[state=active]:bg-lux-gold data-[state=active]:text-lux-black"
              data-testid="tab-users"
            >
              <Users className="w-4 h-4 mr-2" />
              User Management
            </TabsTrigger>
          </TabsList>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Settings Form */}
              <div className="lux-card p-6 lg:p-8">
                <h3 className="font-heading text-xl text-lux-gold mb-6">Company Details</h3>
                <form onSubmit={handleSettingsSubmit} className="space-y-6">
                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <Label className="text-lux-text">Company Logo</Label>
                    <div className="flex items-start gap-4">
                      <div className="w-24 h-24 bg-lux-row border border-lux-border rounded-lg flex items-center justify-center overflow-hidden">
                        <img
                          src={displayedCompanyLogo}
                          alt="Company Logo"
                          className="w-full h-full object-contain"
                          data-testid="settings-company-logo-preview"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="lux-btn-secondary"
                            data-testid="upload-logo-btn"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Upload
                          </Button>
                          {formData.company_logo && (
                            <Button
                              type="button"
                              onClick={handleRemoveLogo}
                              variant="ghost"
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                              data-testid="remove-logo-btn"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-lux-muted">Max 2MB (PNG, JPG)</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-lux-text">Company Name</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="lux-input"
                      placeholder="Company name"
                      data-testid="settings-company-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-lux-text">Company Address</Label>
                    <Input
                      value={formData.company_address}
                      onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                      className="lux-input"
                      placeholder="Company address"
                      data-testid="settings-company-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-lux-text">Phone</Label>
                      <Input
                        value={formData.company_phone}
                        onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                        className="lux-input"
                        placeholder="Phone number"
                        data-testid="settings-company-phone-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-lux-text">Email</Label>
                      <Input
                        type="email"
                        value={formData.company_email}
                        onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                        className="lux-input"
                        placeholder="Email address"
                        data-testid="settings-company-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-lux-text">Company Tagline</Label>
                    <Input
                      value={formData.company_tagline}
                      onChange={(e) => setFormData({ ...formData, company_tagline: e.target.value })}
                      className="lux-input"
                      placeholder="Company tagline"
                      data-testid="settings-company-tagline-input"
                    />
                  </div>

                  {/* Referral Commission Rate */}
                  <div className="space-y-2 p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                    <Label className="text-amber-400 font-medium">Customer Referral Commission Rate (%)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={formData.referral_commission_rate}
                        onChange={(e) => setFormData({ ...formData, referral_commission_rate: e.target.value })}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (isNaN(val) || val < 0) {
                            setFormData({ ...formData, referral_commission_rate: 0 });
                          } else if (val > 100) {
                            setFormData({ ...formData, referral_commission_rate: 100 });
                          }
                        }}
                        className="lux-input w-24"
                        data-testid="settings-referral-commission-input"
                      />
                      <span className="text-lux-muted text-sm">% of sale amount paid to referrers</span>
                    </div>
                    <p className="text-xs text-lux-muted">This rate is used to calculate suggested commission for customer referrals in the Bonus Tracker.</p>
                  </div>

                  <div className="pt-4 border-t border-lux-border">
                    <Button
                      type="submit"
                      disabled={saving}
                      className="lux-btn-primary"
                      data-testid="save-settings-btn"
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-lux-black/30 border-t-lux-black rounded-full animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>

              {/* Preview */}
              <div className="lux-card p-6 lg:p-8">
                <h3 className="font-heading text-xl text-lux-gold mb-6">Invoice Header Preview</h3>
                <div className="text-center p-6 bg-lux-row rounded-lg">
                  <div className="mb-4 flex justify-center">
                    <img
                      src={displayedCompanyLogo}
                      alt="Logo Preview"
                      className="h-16 w-auto max-w-[220px] object-contain"
                      data-testid="settings-invoice-header-logo-preview"
                    />
                  </div>
                  <h2 className="font-heading text-3xl text-lux-gold mb-1">
                    {formData.company_name || 'Company Name'}
                  </h2>
                  <p className="text-lux-muted italic mb-3">
                    {formData.company_tagline || 'Company Tagline'}
                  </p>
                  <p className="text-lux-muted text-sm">
                    {formData.company_address || 'Company Address'}
                  </p>
                  <p className="text-lux-muted text-sm">
                    {formData.company_phone || 'Phone'} • {formData.company_email || 'Email'}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Header with Add Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-xl text-lux-gold">User Management</h3>
                  <p className="text-lux-muted text-sm">Manage system users and their access</p>
                </div>
                <Button onClick={openAddUserModal} className="lux-btn-primary" data-testid="add-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>

              {/* Users Table */}
              <div className="lux-card overflow-hidden">
                {loadingUsers ? (
                  <TableSkeleton rows={5} cols={5} />
                ) : users.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Users className="w-16 h-16 text-lux-border mb-4" />
                    <p className="text-lux-muted text-lg">No users found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="lux-table-header">
                        <tr>
                          <th className="px-6 py-4 text-left">Name</th>
                          <th className="px-6 py-4 text-left">Email</th>
                          <th className="px-6 py-4 text-left">Role</th>
                          <th className="px-6 py-4 text-left">Status</th>
                          <th className="px-6 py-4 text-left">Created</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={user.id || `user-${index}`} className="lux-table-row" data-testid={`user-row-${user.id || index}`}>
                            <td className="px-6 py-4 text-lux-text font-medium">
                              {user.full_name || 'Unknown'}
                              {currentUser && user.id === currentUser.id && (
                                <span className="ml-2 text-xs text-lux-gold">(You)</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-lux-muted">{user.email || '-'}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                user.role === 'md' 
                                  ? 'bg-purple-900/30 text-purple-400 border border-purple-900/50' 
                                  : 'bg-blue-900/30 text-blue-400 border border-blue-900/50'
                              }`}>
                                {user.role === 'md' ? 'Managing Director' : 'Agent'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                user.status === 'active' || !user.status
                                  ? 'bg-green-900/30 text-green-400 border border-green-900/50' 
                                  : 'bg-red-900/30 text-red-400 border border-red-900/50'
                              }`}>
                                {user.status === 'active' || !user.status ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-lux-muted text-sm">
                              {formatDate(user.created_at)}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditUserModal(user)}
                                  className="text-lux-muted hover:text-lux-gold"
                                  data-testid={`edit-user-${user.id || index}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                {currentUser && user.id !== currentUser.id && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openDeactivateDialog(user)}
                                      className={`${
                                        user.status === 'active' || !user.status
                                          ? 'text-lux-muted hover:text-amber-400'
                                          : 'text-lux-muted hover:text-green-400'
                                      }`}
                                      data-testid={`toggle-user-${user.id || index}`}
                                    >
                                      {user.status === 'active' || !user.status ? (
                                        <UserX className="w-4 h-4" />
                                      ) : (
                                        <UserCheck className="w-4 h-4" />
                                      )}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openDeleteDialog(user)}
                                      className="text-lux-muted hover:text-red-400"
                                      data-testid={`delete-user-${user.id || index}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Add/Edit User Modal */}
        <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
          <DialogContent className="bg-lux-card border-lux-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-text">
                {selectedUser ? 'Edit User' : 'Add New User'}
              </DialogTitle>
              <DialogDescription className="text-lux-muted">
                {selectedUser ? 'Update user details below' : 'Enter the details for the new user'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-lux-text">Full Name *</Label>
                <Input
                  value={userFormData.full_name}
                  onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                  className={`lux-input ${userFormErrors.full_name ? 'border-red-500' : ''}`}
                  placeholder="Enter full name"
                  data-testid="user-name-input"
                />
                {userFormErrors.full_name && <p className="text-red-400 text-xs">{userFormErrors.full_name}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-lux-text">Email *</Label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  className={`lux-input ${userFormErrors.email ? 'border-red-500' : ''}`}
                  placeholder="Enter email address"
                  data-testid="user-email-input"
                />
                {userFormErrors.email && <p className="text-red-400 text-xs">{userFormErrors.email}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-lux-text">Role *</Label>
                  <Select
                    value={userFormData.role}
                    onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}
                  >
                    <SelectTrigger className={`lux-input ${userFormErrors.role ? 'border-red-500' : ''}`} data-testid="user-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="md">Managing Director</SelectItem>
                    </SelectContent>
                  </Select>
                  {userFormErrors.role && <p className="text-red-400 text-xs">{userFormErrors.role}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-lux-text">Status</Label>
                  <Select
                    value={userFormData.status}
                    onValueChange={(value) => setUserFormData({ ...userFormData, status: value })}
                  >
                    <SelectTrigger className="lux-input" data-testid="user-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-lux-text">
                  {selectedUser ? 'New Password (leave blank to keep current)' : 'Temporary Password *'}
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                    className={`lux-input pr-10 ${userFormErrors.password ? 'border-red-500' : ''}`}
                    placeholder={selectedUser ? 'Enter new password' : 'Enter temporary password'}
                    data-testid="user-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-lux-muted hover:text-lux-text"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {userFormErrors.password && <p className="text-red-400 text-xs">{userFormErrors.password}</p>}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUserModalOpen(false)}
                className="lux-btn-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUserSubmit}
                disabled={savingUser}
                className="lux-btn-primary"
                data-testid="save-user-btn"
              >
                {savingUser ? 'Saving...' : selectedUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-lux-card border-lux-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lux-text">Delete User</AlertDialogTitle>
              <AlertDialogDescription className="text-lux-muted">
                Are you sure you want to delete "{selectedUser?.full_name}"? This will permanently remove their account and any associated agent profile. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="lux-btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 text-white">
                Delete User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Deactivate/Activate User Confirmation */}
        <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
          <AlertDialogContent className="bg-lux-card border-lux-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lux-text">
                {selectedUser?.status === 'active' || !selectedUser?.status ? 'Deactivate' : 'Activate'} User
              </AlertDialogTitle>
              <AlertDialogDescription className="text-lux-muted">
                {selectedUser?.status === 'active' || !selectedUser?.status 
                  ? `Are you sure you want to deactivate "${selectedUser?.full_name}"? They will not be able to log in until reactivated.`
                  : `Are you sure you want to activate "${selectedUser?.full_name}"? They will be able to log in again.`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="lux-btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleToggleUserStatus} 
                className={selectedUser?.status === 'active' || !selectedUser?.status 
                  ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
                }
              >
                {selectedUser?.status === 'active' || !selectedUser?.status ? 'Deactivate' : 'Activate'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
